import type { Message, Part, Session } from "@mux/sdk/v2/client";
import { createTextGapRecovery } from "./text-gap-recovery";
import { parsePartDeltaEvent } from "../event-validation";
import { batch } from "solid-js";
import { messageTurnDiff, type TurnDiffMessage, type VersionedTurnDiff } from "@/transport/turn-diffs";
import { eventCursorReached } from "@/utils/event-cursor";
import {
  createSnapshotChanges,
  reconcileTranscriptSnapshot,
  type SnapshotChanges,
} from "./snapshot-reconciliation";
import { createStore, produce } from "solid-js/store";
import type { TranscriptReplicaRepository } from "@/storage/transcript-replica";
import { decodeTranscriptReset, type TranscriptReset } from "@/transport/transcript-reset";
import { readSessionSnapshot } from "@/transport/session-read";
import type { SessionPrefetchStore } from "../session-prefetch";
import type { SessionInfoClient, SessionMessagesClient } from "./contracts";
import {
  cmpMessage,
  isSessionNotFoundError,
  mergeMessagesSorted,
  sortParts,
} from "./message-reducer";
import type { createSessionOptimisticController } from "./optimistic-controller";

type OptimisticController = ReturnType<
  typeof createSessionOptimisticController
>;

type Page = {
  session: Message[];
  part: Array<{ id: string; part: Part[] }>;
  cursor?: string;
  complete: boolean;
};

export type TranscriptItems = Array<{ info: Message; parts: Part[] }>;

export type TranscriptSnapshot = {
  items: TranscriptItems;
  complete: boolean;
  cursor?: string;
};

type TranscriptAuthority = "provisional" | "replica" | "server";

/** The fence of a provisional chat's in-flight activation request. */
export type TranscriptActivation = {
  changes: SnapshotChanges;
  finish(): void;
  /** A reset or stream gap that the response may predate invalidated it. */
  superseded: boolean;
};

/** Server time of the session's last transcript reset; older messages were deleted. */
function transcriptResetAt(session: Session) {
  return (session.time as { reset?: number }).reset ?? 0;
}

/** An update to an assistant message, tool call, or text that has not finished. */
function streamingEvent(event: { type: string; properties?: unknown }) {
  if (event.type === "message.updated") {
    const info = (event.properties as { info?: Message } | undefined)?.info;
    return info?.role === "assistant" && typeof info.time.completed !== "number";
  }
  if (event.type !== "message.part.updated") return false;
  const part = (event.properties as { part?: Part } | undefined)?.part;
  if (part?.type === "tool") return part.state.status === "pending" || part.state.status === "running";
  if (part?.type === "text" || part?.type === "reasoning") return !!part.time && part.time.end === undefined;
  return false;
}

export function createSessionTranscriptController(input: {
  messagesClientFor(directory: string): SessionMessagesClient;
  infoClientFor(directory: string): SessionInfoClient;
  getMessages(sessionID: string): Message[] | undefined;
  getParts(messageID: string): Part[] | undefined;
  getSession(sessionID: string): Session | undefined;
  findSessionIDForMessage(messageID: string): string | undefined;
  setMessages(sessionID: string, messages: Message[] | undefined): void;
  setMessage(sessionID: string, index: number, message: Message): void;
  setParts(messageID: string, parts: Part[]): void;
  removeParts(messageIDs: readonly string[]): void;
  upsertSession(session: Session): void;
  tracked(directory: string, sessionID: string): boolean;
  touch(directory: string, sessionID: string): void;
  markMissing(directory: string, sessionID: string): void;
  optimistic: OptimisticController;
  prefetch: SessionPrefetchStore;
  replica: TranscriptReplicaRepository;
  replicaScope(): string;
  /** Whether the event stream delivers every event of this chat from now on. */
  eventsLive(sessionID: string): boolean;
  /** The last event the stream committed; a request sent now is handled after it. */
  streamCursor(): string | undefined;
}) {
  // A message is a whole turn's prompt or reply, and earlier turns still cost
  // tens of KB each, so a page is sized in turns: opening shows the latest six
  // and scrolling up loads twenty more. Jumping to a message takes bigger pages
  // to reach it in fewer round trips.
  const messagePageSize = 12;
  const scrollbackPageSize = 40;
  const historyPageSize = 200;
  const inflight = new Map<string, Promise<void>>();
  const forced = new Map<string, Promise<void>>();
  const latestLoads = new Map<string, Promise<void>>();
  const lifetimes = new Map<string, AbortController>();
  // The stream cursor each in-flight latest-window load was requested at.
  const pendingRequests = new Map<string, { cursor: string }>();
  // Chats whose machine replaced the transcript: their next latest-window load
  // replaces what is loaded instead of merging into it.
  const replaced = new Set<string>();
  const snapshots = new Map<string, Set<SnapshotChanges>>();
  const lifetime = (id: string) => {
    let owner = lifetimes.get(id);
    if (!owner) lifetimes.set(id, (owner = new AbortController()));
    return owner;
  };
  const invalidateRequests = (id: string) => {
    lifetimes.get(id)?.abort();
    lifetimes.delete(id);
    snapshots.delete(id);
    const activation = activations.get(id);
    if (activation) activation.superseded = true;
    inflight.delete(id);
    forced.delete(id);
    latestLoads.delete(id);
    messageLoads.delete(id);
    replicaLoads.delete(id);
    // A cancelled load no longer makes the transcript current (see currentSince).
    pendingRequests.delete(id);
  };
  const beginSnapshot = (id: string) => {
    const changes = createSnapshotChanges();
    let reads = snapshots.get(id);
    if (!reads) snapshots.set(id, (reads = new Set()));
    reads.add(changes);
    return {
      changes,
      finish: () => {
        reads.delete(changes);
        if (!reads.size && snapshots.get(id) === reads) snapshots.delete(id);
      },
    };
  };
  // A provisional chat's transcript arrives with its activation response. Its
  // fence starts before that request, so the response is reconciled like an
  // HTTP load instead of rolling back events that reached the client first.
  // It ends when the response installs or the chat is confirmed or dropped.
  const activations = new Map<string, TranscriptActivation>();
  const endActivation = (id: string) => {
    const activation = activations.get(id);
    if (!activation) return;
    activations.delete(id);
    activation.finish();
    return activation;
  };
  const messageLoads = new Map<string, Promise<void>>();
  const replicaLoads = new Map<string, Promise<boolean>>();
  const replicaWrites = new Map<string, { settled: boolean; timer: ReturnType<typeof setTimeout> }>();
  // Reset markers fetched after (re)connecting without event replay. A client
  // that missed a reset receipt uses them to drop cached messages the server
  // already deleted, before those messages can be painted.
  const resetMarkers = new Map<string, number>();
  let resetReconciliation: AbortController | undefined;
  const [meta, setMeta] = createStore({
    limit: {} as Record<string, number | undefined>,
    cursor: {} as Record<string, string | undefined>,
    complete: {} as Record<string, boolean | undefined>,
    loading: {} as Record<string, boolean | undefined>,
    loaded: {} as Record<string, boolean | undefined>,
    authority: {} as Record<string, TranscriptAuthority | undefined>,
    updated: {} as Record<string, number | undefined>,
    attempted: {} as Record<string, number | undefined>,
    prepending: {} as Record<string, boolean | undefined>,
    stale: {} as Record<string, boolean | undefined>,
    // The stream cursor when the server snapshot now shown was requested.
    requested: {} as Record<string, string | undefined>,
  });
  let disposed = false;
  const key = (directory: string, sessionID: string) =>
    `${directory}\n${sessionID}`;
  // Only the event stream keeps a transcript up to date after its snapshot. A
  // replica, a stream gap, or a chat the stream does not watch (or has not
  // caught up on) leaves it possibly behind the server.
  const transcriptCurrent = (directory: string, sessionID: string) => {
    const id = key(directory, sessionID);
    return (
      input.getMessages(sessionID) !== undefined &&
      meta.loaded[id] === true &&
      meta.authority[id] === "server" &&
      !meta.stale[id] &&
      input.eventsLive(sessionID)
    );
  };
  // A server snapshot requested after the page had applied event `since` holds
  // every event up to it, and a stream that resumed from `since` delivers every
  // later one, so a reconnect that resumed there leaves it current. That holds
  // for such a snapshot still loading, once it lands.
  const currentSince = (directory: string, sessionID: string, since?: string) => {
    const id = key(directory, sessionID);
    const pending = pendingRequests.get(id);
    if (pending !== undefined) return eventCursorReached(pending.cursor, since);
    return (
      input.getMessages(sessionID) !== undefined &&
      meta.loaded[id] === true &&
      meta.authority[id] === "server" &&
      !meta.stale[id] &&
      eventCursorReached(meta.requested[id], since)
    );
  };
  const textRecovery = createTextGapRecovery({
    load: (directory, sessionID) => controller.sync(directory, sessionID, { force: true }),
    getParts: input.getParts,
    setParts: input.setParts,
    report: (error) => console.warn("Failed to recover streaming text", error),
  });
  const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
  // A latest-window snapshot that kept older loaded history keeps paging from
  // the loaded window's cursor rather than its own. A loaded window whose
  // cursor is unknown (a trimmed chat or a replica of its latest window) pages
  // from the snapshot's cursor; paging merges the retained messages by identity.
  const loadedWindow = (
    id: string,
    retainedHistory: boolean,
    next: { cursor?: string; complete: boolean },
  ) =>
    retainedHistory && meta.loaded[id] && (meta.cursor[id] !== undefined || meta.complete[id])
      ? { cursor: meta.cursor[id], complete: meta.complete[id] ?? false }
      : { cursor: next.cursor, complete: next.complete };

  // The replica holds the chat's latest window, the page a fresh open loads
  // first, copied when it is written so no second copy stays in memory.
  const persistReplica = async (directory: string, sessionID: string) => {
    if (disposed || !input.replica.available) return;
    const id = key(directory, sessionID);
    if (meta.authority[id] !== "server") return;
    const messages = input.getMessages(sessionID);
    if (!messages || !meta.loaded[id]) return;
    const window = messages.slice(-messagePageSize);
    // Without the older history the window's own cursor is unknown; the load
    // that revalidates a restored replica supplies it.
    const whole = window.length === messages.length;
    const copy = clone({
      session: input.getSession(sessionID),
      messages: window,
      parts: window.map((message) => ({ messageID: message.id, parts: input.getParts(message.id) ?? [] })),
    });
    await input.replica.write({
      server: input.replicaScope(),
      directory,
      sessionID,
      ...copy,
      cursor: whole ? meta.cursor[id] : undefined,
      complete: whole && (meta.complete[id] ?? false),
      capturedAt: Date.now(),
    });
  };

  // A settled change is written promptly; one still streaming at most about
  // once a second. A pending write is only ever brought forward, so a steady
  // stream cannot postpone it.
  const scheduleReplicaWrite = (directory: string, sessionID: string, settled = true) => {
    if (disposed || !input.replica.available) return;
    const id = key(directory, sessionID);
    const current = replicaWrites.get(id);
    if (current && (current.settled || !settled)) return;
    if (current) clearTimeout(current.timer);
    replicaWrites.set(id, {
      settled,
      timer: setTimeout(() => {
        replicaWrites.delete(id);
        void persistReplica(directory, sessionID);
      }, settled ? 100 : 1_000),
    });
  };

  const hydrateReplica = (directory: string, sessionID: string) => {
    if (!input.replica.available) return Promise.resolve(false);
    const id = key(directory, sessionID);
    const current = replicaLoads.get(id);
    if (current) return current;
    const owner = lifetime(id);
    const request = (async () => {
      const replica = await input.replica.read(
        input.replicaScope(),
        directory,
        sessionID,
      );
      if (
        !replica ||
        disposed ||
        owner.signal.aborted ||
        !input.tracked(directory, sessionID) ||
        input.getMessages(sessionID) !== undefined
      )
        return false;
      const resetAt = resetMarkers.get(id) ?? 0;
      const deleted = new Set(
        replica.messages
          .filter((message) => message.time.created < resetAt)
          .map((message) => message.id),
      );
      const messages = deleted.size
        ? replica.messages.filter((message) => !deleted.has(message.id))
        : replica.messages;
      // Everything older than a deleted message was deleted with it.
      const cursor = deleted.size ? undefined : replica.cursor;
      const complete = deleted.size > 0 || replica.complete;
      batch(() => {
        // Replica metadata may race the authoritative sidebar/bootstrap load.
        // Use it only to fill a missing deep-link record; never let a stale
        // cache replace session metadata that the server already supplied.
        if (replica.session && !input.getSession(sessionID))
          input.upsertSession(replica.session);
        input.setMessages(sessionID, messages);
        for (const item of replica.parts)
          if (!deleted.has(item.messageID))
            input.setParts(item.messageID, item.parts);
        setMeta("limit", id, messages.length);
        setMeta("cursor", id, cursor);
        setMeta("complete", id, complete);
        setMeta("loaded", id, true);
        setMeta("authority", id, "replica");
        setMeta("updated", id, replica.capturedAt);
        input.prefetch.set({
          directory,
          sessionID,
          limit: messages.length,
          cursor,
          complete,
        });
      });
      if (deleted.size) {
        void input.replica
          .reset(input.replicaScope(), directory, sessionID, [...deleted])
          .catch((error) =>
            console.warn("Failed to prune a reset transcript replica", error),
          );
      }
      return true;
    })().finally(() => {
      if (replicaLoads.get(id) === request) replicaLoads.delete(id);
    });
    replicaLoads.set(id, request);
    return request;
  };

  const sessionIDForEvent = (event: { type: string; properties?: unknown }) => {
    if (!event.type.startsWith("message.")) return;
    const props = event.properties as
      | {
          sessionID?: string;
          messageID?: string;
          info?: { sessionID?: string };
          part?: { sessionID?: string; messageID?: string };
        }
      | undefined;
    return (
      props?.sessionID ??
      props?.info?.sessionID ??
      props?.part?.sessionID ??
      input.findSessionIDForMessage(
        props?.messageID ?? props?.part?.messageID ?? "",
      )
    );
  };

  const fetchMessages = async (value: {
    client: SessionMessagesClient;
    sessionID: string;
    limit: number;
    before?: string;
    signal?: AbortSignal;
  }): Promise<Page> => {
    const response = await readSessionSnapshot(
      (signal) =>
        value.client.session.messages(
          {
            sessionID: value.sessionID,
            limit: value.limit,
            before: value.before,
            $query_outline: 1,
          },
          { signal },
        ),
      value.signal,
    );
    const items = (response.data ?? []).filter((item) => !!item?.info?.id);
    const session = items.map((item) => item.info).sort(cmpMessage);
    const part = items.map((item) => ({
      id: item.info.id,
      part: sortParts(item.parts),
    }));
    const cursor = response.response.headers.get("x-next-cursor") ?? undefined;
    return { session, part, cursor, complete: !cursor };
  };

  const applyPrefetch = (directory: string, sessionID: string) => {
    const id = key(directory, sessionID);
    const seeded = input.prefetch.get(directory, sessionID);
    if (
      !seeded ||
      input.getMessages(sessionID) === undefined ||
      meta.limit[id] !== undefined
    )
      return;
    batch(() => {
      setMeta("limit", id, seeded.limit);
      setMeta("cursor", id, seeded.cursor);
      setMeta("complete", id, seeded.complete);
      setMeta("loading", id, false);
      setMeta("loaded", id, true);
      setMeta("authority", id, "server");
    });
  };

  const loadMessages = async (value: {
    directory: string;
    sessionID: string;
    limit: number;
    before?: string;
    mode?: "replace" | "prepend";
  }) => {
    const id = key(value.directory, value.sessionID);
    if (disposed) return;

    // Concurrent sync/prefetch callers must await the in-flight load instead of
    // no-oping while meta.loading is true — that left messagesReady false forever.
    // A replaced transcript still loads after a load the replacement cut short.
    const pending = messageLoads.get(id);
    if (pending) {
      await pending;
      if (value.mode !== "prepend" && !replaced.has(id)) return;
      if (value.mode === "prepend" && (disposed || meta.loading[id] || meta.complete[id] || !meta.cursor[id]))
        return;
    }
    // The cursor and older pages of a transcript its machine replaced predate
    // the replacement: load its first page again instead of an older one.
    if (value.mode === "prepend" && replaced.has(id)) {
      value = { directory: value.directory, sessionID: value.sessionID, limit: messagePageSize };
    }

    const owner = lifetime(id);
    const snapshot = beginSnapshot(id);
    // A transcript its machine replaced is taken as the server's first page
    // alone: nothing loaded before it is kept or reloaded.
    const replace = value.mode !== "prepend" && replaced.has(id);
    const recoverLimit = meta.loaded[id] && !replace ? (meta.limit[id] ?? 0) : 0;
    const request = (async () => {
      setMeta("loading", id, true);
      if (value.mode === "prepend") setMeta("prepending", id, true);
      const requested = value.mode === "prepend" ? undefined : input.streamCursor();
      const pending = requested === undefined ? undefined : { cursor: requested };
      if (pending) pendingRequests.set(id, pending);
      try {
        const page = await fetchMessages({
          client: input.messagesClientFor(value.directory),
          sessionID: value.sessionID,
          limit: value.limit,
          before: value.before,
          signal: owner.signal,
        });
        // Recover the loaded window across the server's bounded pages.
        while (
          value.mode !== "prepend" &&
          page.cursor &&
          page.session.length < recoverLimit &&
          !owner.signal.aborted
        ) {
          const cursor = page.cursor;
          const older = await fetchMessages({
            client: input.messagesClientFor(value.directory),
            sessionID: value.sessionID,
            limit: recoverLimit - page.session.length,
            before: cursor,
            signal: owner.signal,
          });
          if (older.cursor === cursor)
            throw new Error("Transcript recovery made no progress");
          page.session = mergeMessagesSorted(older.session, page.session);
          page.part.push(...older.part);
          page.cursor = older.cursor;
          page.complete = older.complete;
        }
        if (
          disposed ||
          owner.signal.aborted ||
          !input.tracked(value.directory, value.sessionID)
        )
          return;
        const next = input.optimistic.merge(
          value.directory,
          value.sessionID,
          page,
        );
        const { messages, parts, removed, retainedHistory } =
          reconcileTranscriptSnapshot({
            live: input.getMessages(value.sessionID) ?? [],
            incoming: next.session,
            parts: next.part,
            liveParts: input.getParts,
            changes: snapshot.changes,
            prepend: value.mode === "prepend",
            replace,
            complete: next.complete,
          });
        const { cursor, complete } = loadedWindow(
          id,
          value.mode !== "prepend" && retainedHistory,
          next,
        );
        batch(() => {
          if (replace) replaced.delete(id);
          if (removed.length) input.removeParts(removed);
          input.setMessages(value.sessionID, messages);
          for (const item of parts) input.setParts(item.id, item.part);
          setMeta("limit", id, messages.length);
          setMeta("cursor", id, cursor);
          setMeta("complete", id, complete);
          setMeta("loaded", id, true);
          if (value.mode !== "prepend") {
            setMeta("authority", id, "server");
            setMeta("stale", id, false);
            setMeta("requested", id, requested);
          }
          const session = input.getSession(value.sessionID);
          setMeta(
            "updated",
            id,
            session
              ? (session.time.updated ?? session.time.created)
              : undefined,
          );
          input.prefetch.set({
            directory: value.directory,
            sessionID: value.sessionID,
            limit: messages.length,
            cursor,
            complete,
          });
        });
        scheduleReplicaWrite(value.directory, value.sessionID);
      } catch (error) {
        if (!disposed && !owner.signal.aborted) throw error;
      } finally {
        if (pending && pendingRequests.get(id) === pending) pendingRequests.delete(id);
        snapshot.finish();
        if (!owner.signal.aborted)
          setMeta(
            produce((draft) => {
              if (
                disposed ||
                !input.tracked(value.directory, value.sessionID)
              ) {
                delete draft.loading[id];
                delete draft.prepending[id];
              } else {
                draft.loading[id] = false;
                draft.prepending[id] = false;
              }
            }),
          );
      }
    })();

    messageLoads.set(id, request);
    try {
      await request;
    } finally {
      if (messageLoads.get(id) === request) messageLoads.delete(id);
    }
  };

  const clear = (directory: string, sessionIDs: Iterable<string>) => {
    const ids = [...sessionIDs];
    if (ids.length === 0) return;
    for (const sessionID of ids) {
      const id = key(directory, sessionID);
      invalidateRequests(id);
      activations.delete(id);
      replaced.delete(id);
      textRecovery.clear(directory, sessionID);
      clearTimeout(replicaWrites.get(id)?.timer);
      replicaWrites.delete(id);
    }
    setMeta(
      produce((draft) => {
        for (const sessionID of ids) {
          const id = key(directory, sessionID);
          delete draft.limit[id];
          delete draft.cursor[id];
          delete draft.complete[id];
          delete draft.loading[id];
          delete draft.loaded[id];
          delete draft.authority[id];
          delete draft.updated[id];
          delete draft.attempted[id];
          delete draft.prepending[id];
          delete draft.stale[id];
        }
      }),
    );
  };

  // Force the next sync to refetch without unloading what is already rendered.
  // Dropping `loaded` here would blank a surface showing correct content
  // (optimistic or replica) whenever a revalidation becomes due, such as when a
  // provisional session is promoted. With `since`, a stream resumed there:
  // transcripts current since it are kept.
  const markStale = (directory: string, sessionIDs: Iterable<string>, since?: string) => {
    const ids = [...sessionIDs].filter((sessionID) => !currentSince(directory, sessionID, since));
    if (ids.length === 0) return;
    input.prefetch.clear(directory, ids);
    for (const sessionID of ids) invalidateRequests(key(directory, sessionID));
    setMeta(
      produce((draft) => {
        for (const sessionID of ids) {
          const id = key(directory, sessionID);
          draft.stale[id] = true;
          delete draft.requested[id];
          draft.loading[id] = false;
          draft.prepending[id] = false;
          delete draft.updated[id];
          delete draft.attempted[id];
        }
      }),
    );
  };

  const run = (id: string, task: () => Promise<void>) => {
    const current = inflight.get(id);
    if (current) return current;
    const request = task().finally(() => {
      if (inflight.get(id) === request) inflight.delete(id);
    });
    inflight.set(id, request);
    return request;
  };

  const reset = (directory: string, receipt: TranscriptReset): Promise<void> => {
    if (disposed) return Promise.resolve();
    const { sessionID, messageIDs } = receipt;
    for (const messageID of messageIDs) textRecovery.removed(directory, sessionID, messageID);
    const id = key(directory, sessionID);
    const removed = new Set(messageIDs);
    const messages = input.getMessages(sessionID);
    const remaining = messages?.filter((message) => !removed.has(message.id));
    const changed = remaining !== undefined && remaining.length !== messages!.length;
    const recover = (!!meta.loading[id] && meta.authority[id] !== "server") ||
      (changed && remaining!.length > 0 && !meta.complete[id]);
    invalidateRequests(id);
    clearTimeout(replicaWrites.get(id)?.timer);
    replicaWrites.delete(id);
    input.prefetch.clear(directory, [sessionID]);
    batch(() => {
      input.optimistic.forgetMessages(directory, sessionID, messageIDs);
      input.removeParts(messageIDs);
      setMeta("loading", id, false);
      setMeta("prepending", id, false);
      if (changed) {
        input.setMessages(sessionID, remaining!);
        setMeta("limit", id, remaining!.length);
        setMeta("cursor", id, undefined);
        setMeta("complete", id, true);
      }
    });
    // Queue the durable deletion immediately, behind already captured writes.
    // This also fixes replicas for sessions not currently loaded in memory.
    const persisted = input.replica.reset(input.replicaScope(), directory, sessionID, messageIDs);
    if (messages && meta.authority[id] === "server") scheduleReplicaWrite(directory, sessionID);
    // An initial load interrupted by the reset must still finish loading, rather
    // than leaving the mounted surface permanently waiting for its transcript.
    if (recover && input.tracked(directory, sessionID)) {
      return Promise.all([persisted, loadMessages({ directory, sessionID, limit: messagePageSize })]).then(() => {});
    }
    return persisted;
  };

  const loadedTranscripts = () =>
    Object.keys(meta.loaded).flatMap((id) => {
      if (!meta.loaded[id]) return [];
      const separator = id.indexOf("\n");
      return [{ directory: id.slice(0, separator), sessionID: id.slice(separator + 1) }];
    });

  // A live receipt deletes everything up to now, so a load it cancels has
  // nothing left to bring. A missed reset only deleted messages created before
  // its marker; a latest-window load cancelled by pruning may be the only way
  // newer messages arrive, so it restarts.
  const pruneMissedReset = (directory: string, sessionID: string, resetAt: number) => {
    const deleted = (input.getMessages(sessionID) ?? [])
      .filter(
        (message) =>
          message.time.created < resetAt &&
          // A prompt awaiting confirmation carries a client timestamp.
          !input.optimistic.has(directory, sessionID, message.id),
      )
      .map((message) => message.id);
    if (!deleted.length) return Promise.resolve();
    const id = key(directory, sessionID);
    const reload = !!meta.loading[id] && !meta.prepending[id];
    const pruned = reset(directory, { sessionID, messageIDs: deleted });
    if (!reload) return pruned;
    return Promise.all([
      pruned,
      loadMessages({ directory, sessionID, limit: messagePageSize }),
    ]).then(() => undefined);
  };

  const reconcileResets = async (
    loadSessions: (sessionIDs: string[], signal: AbortSignal) => Promise<Session[]>,
  ) => {
    if (disposed) return;
    resetReconciliation?.abort();
    const owner = (resetReconciliation = new AbortController());
    try {
      const replicas = await input.replica.list(input.replicaScope());
      const sessionIDs = new Set([
        ...replicas.map((item) => item.sessionID),
        ...loadedTranscripts().map((item) => item.sessionID),
      ]);
      if (disposed || owner.signal.aborted || sessionIDs.size === 0) return;
      const sessions = await loadSessions([...sessionIDs], owner.signal);
      if (disposed || owner.signal.aborted) return;
      const markers = new Map(
        sessions.flatMap((session) => {
          const resetAt = transcriptResetAt(session);
          return resetAt ? [[session.id, resetAt] as const] : [];
        }),
      );
      for (const { directory, sessionID } of replicas) {
        const resetAt = markers.get(sessionID);
        if (resetAt) resetMarkers.set(key(directory, sessionID), resetAt);
      }
      // Read loaded transcripts now: replicas hydrated during the request count.
      const pruned = loadedTranscripts().flatMap(({ directory, sessionID }) => {
        const resetAt = markers.get(sessionID);
        if (!resetAt) return [];
        resetMarkers.set(key(directory, sessionID), resetAt);
        return [pruneMissedReset(directory, sessionID, resetAt)];
      });
      await Promise.all(pruned);
    } finally {
      if (resetReconciliation === owner) resetReconciliation = undefined;
    }
  };

  const controller = {
    clear,
    markStale,
    currentSince,
    /**
     * The event through which this page holds every event of the chat, for
     * the stream to replay what followed: when its server snapshot was
     * requested. A chat it holds no snapshot of is loaded after the current
     * event; a snapshot of unknown age has none.
     */
    watchSince(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      const pending = pendingRequests.get(id);
      if (pending) return pending.cursor;
      const held = input.getMessages(sessionID) !== undefined && meta.loaded[id] === true && meta.authority[id] === "server" && !meta.stale[id];
      return held ? meta.requested[id] : input.streamCursor();
    },
    recoverText: textRecovery.receive,
    reset,
    reconcileResets,
    persistEvent(
      directory: string,
      event: { type: string; properties?: unknown },
    ) {
      if (event.type === "session.transcript.reset") {
        return reset(directory, decodeTranscriptReset(event.properties));
      }
      if (event.type === "session.transcript.replaced") {
        // The machine replaced the transcript with a copy another machine wrote,
        // such as the account's Mux Oracle conversation: what is loaded is
        // behind, and messages the copy dropped must go with it.
        const sessionID = (event.properties as { sessionID?: unknown } | undefined)?.sessionID;
        if (typeof sessionID !== "string" || !sessionID) return;
        replaced.add(key(directory, sessionID));
        markStale(directory, [sessionID]);
        if (input.tracked(directory, sessionID)) return loadMessages({ directory, sessionID, limit: messagePageSize });
        return;
      }
      if (event.type === "session.diff.updated") {
        const props = event.properties as { sessionID: string; turnSummary?: VersionedTurnDiff & { messageID: string } };
        const update = props.turnSummary;
        const messages = input.getMessages(props.sessionID);
        if (!update || !messages) return;
        const index = messages.findIndex((item) => item.id === update.messageID && item.role === "user");
        const message = messages[index];
        if (!message || (messageTurnDiff(message)?.revision ?? -1) >= update.revision) return;
        const next: TurnDiffMessage = { ...message, turnDiff: { revision: update.revision, summary: update.summary } };
        input.setMessage(props.sessionID, index, next);
        // The server summarized the turn, so it owns the message; a late rollback must not erase it.
        input.optimistic.forgetMessages(directory, props.sessionID, [message.id]);
        scheduleReplicaWrite(directory, props.sessionID);
        return;
      }
      const sessionID = sessionIDForEvent(event);
      if (!sessionID) return;
      const props = event.properties as
        | {
            messageID?: string;
            partID?: string;
            info?: { id?: string };
            part?: { id?: string; messageID?: string };
          }
        | undefined;
      const messageID =
        props?.messageID ?? props?.part?.messageID ?? props?.info?.id;
      const delta = parsePartDeltaEvent(event);
      // Text deltas are append-only coverage, not edits to message metadata or
      // structure. Rejected/duplicate deltas must not fence out a repairing snapshot.
      if (messageID && !(delta?.properties.field === "text"))
        for (const changes of snapshots.get(key(directory, sessionID)) ?? []) {
          changes.messages.add(messageID);
          if (
            event.type !== "message.updated" &&
            event.type !== "message.removed"
          ) {
            const partID = props?.partID ?? props?.part?.id;
            if (!partID) continue;
            let parts = changes.parts.get(messageID);
            if (!parts) changes.parts.set(messageID, (parts = new Set()));
            parts.add(partID);
          }
        }
      if (messageID) {
        if (event.type === "message.removed" || event.type === "message.part.removed") {
          textRecovery.removed(directory, sessionID, messageID, props?.partID);
          // The machine ended a prompt this client sent before it ran; the
          // next snapshot must not restore the client's pending copy.
          if (event.type === "message.removed") input.optimistic.forgetMessages(directory, sessionID, [messageID]);
        } else if (event.type === "message.updated") {
          // The server's copy now owns a prompt this client showed early, as a
          // loaded page's would; a late rollback, such as a Send now preview
          // ending, must not erase it.
          input.optimistic.forgetMessages(directory, sessionID, [messageID]);
        } else if (event.type === "message.part.updated" || delta?.properties.field === "text") {
          textRecovery.updated(directory, sessionID, messageID);
        }
      }
      // The server positions and replays deltas, and checkpoints restore them
      // (sync rule 12), so a delta alone never rewrites the replica.
      if (event.type !== "message.part.delta") scheduleReplicaWrite(directory, sessionID, !streamingEvent(event));
    },
    removeReplica(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      clearTimeout(replicaWrites.get(id)?.timer);
      replicaWrites.delete(id);
      void input.replica.remove(input.replicaScope(), directory, sessionID);
    },
    // A chat no surface shows keeps only its latest window, as a fresh open
    // loads it: older messages and their parts are dropped, and pagination
    // restarts, so the next sync reloads the first page and its cursor. The
    // window reaches back to cover incomplete assistant messages and pending
    // prompts, which are never dropped (sync rule 3).
    trimToLatest(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      const messages = input.getMessages(sessionID);
      if (disposed || !messages || !meta.loaded[id] || meta.authority[id] === "provisional") return;
      const pinned = messages.findIndex((message) =>
        (message.role === "assistant" && typeof message.time.completed !== "number") ||
        input.optimistic.has(directory, sessionID, message.id));
      const start = Math.min(
        Math.max(0, messages.length - messagePageSize),
        pinned === -1 ? messages.length : pinned,
      );
      // An older page still loading would land above the kept window.
      if (start === 0 && !meta.prepending[id]) return;
      invalidateRequests(id);
      input.prefetch.clear(directory, [sessionID]);
      batch(() => {
        input.removeParts(messages.slice(0, start).map((message) => message.id));
        input.setMessages(sessionID, messages.slice(start));
        setMeta("limit", id, undefined);
        setMeta("cursor", id, undefined);
        setMeta("complete", id, false);
        setMeta("loading", id, false);
        setMeta("prepending", id, false);
      });
    },
    installProvisional(
      directory: string,
      sessionID: string,
      items: TranscriptItems,
    ) {
      const id = key(directory, sessionID);
      if (meta.authority[id] === "server" || meta.authority[id] === "replica")
        return;
      const messages = items.map((item) => item.info).sort(cmpMessage);
      const kept = new Set(messages.map((message) => message.id));
      const removed = (input.getMessages(sessionID) ?? [])
        .filter((message) => !kept.has(message.id))
        .map((message) => message.id);
      batch(() => {
        if (removed.length) input.removeParts(removed);
        input.setMessages(sessionID, messages);
        for (const item of items)
          input.setParts(item.info.id, sortParts(item.parts));
        setMeta("limit", id, messages.length);
        setMeta("cursor", id, undefined);
        setMeta("complete", id, false);
        setMeta("loaded", id, true);
        setMeta("authority", id, "provisional");
      });
    },
    installServerSnapshot(
      directory: string,
      sessionID: string,
      snapshot: TranscriptSnapshot,
      activation?: TranscriptActivation,
    ) {
      const id = key(directory, sessionID);
      if (activation?.superseded) {
        // The response may predate a reset or stream gap. Like an aborted HTTP
        // load, it installs nothing; the next sync loads the transcript.
        setMeta("stale", id, true);
        return;
      }
      // A prompt sent while the chat was provisional is still pending; keep it
      // under the same merge rule as fetched pages.
      const page = input.optimistic.merge(directory, sessionID, {
        session: snapshot.items.map((item) => item.info),
        part: snapshot.items.map((item) => ({ id: item.info.id, part: item.parts })),
        cursor: snapshot.cursor,
        complete: snapshot.complete,
      });
      // Without an activation fence the snapshot's age is unknown; only the
      // ordinary reconciliation rules apply.
      const { messages, parts, removed, retainedHistory } =
        reconcileTranscriptSnapshot({
          live: input.getMessages(sessionID) ?? [],
          incoming: page.session.sort(cmpMessage),
          parts: page.part,
          liveParts: input.getParts,
          changes: activation?.changes ?? createSnapshotChanges(),
          complete: page.complete,
        });
      const { cursor, complete } = loadedWindow(id, retainedHistory, page);
      textRecovery.clear(directory, sessionID);
      invalidateRequests(id);
      batch(() => {
        if (removed.length) input.removeParts(removed);
        input.setMessages(sessionID, messages);
        for (const item of parts) input.setParts(item.id, item.part);
        setMeta("limit", id, messages.length);
        // `stale` stays: a pushed snapshot cannot prove it covers a stream gap.
        setMeta("loading", id, false);
        setMeta("prepending", id, false);
        setMeta("cursor", id, cursor);
        setMeta("complete", id, complete);
        setMeta("loaded", id, true);
        setMeta("authority", id, "server");
        const session = input.getSession(sessionID);
        setMeta(
          "updated",
          id,
          session ? (session.time.updated ?? session.time.created) : undefined,
        );
        input.prefetch.set({
          directory,
          sessionID,
          limit: messages.length,
          cursor,
          complete,
        });
      });
      scheduleReplicaWrite(directory, sessionID);
    },
    /** Ends the chat's activation and returns its fence for the response to install. */
    takeActivation(directory: string, sessionID: string) {
      return endActivation(key(directory, sessionID));
    },
    messagesReady(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      return (
        input.getMessages(sessionID) !== undefined && meta.loaded[id] === true
      );
    },
    messagesAuthoritative(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      return (
        input.getMessages(sessionID) !== undefined &&
        meta.loaded[id] === true &&
        meta.authority[id] === "server"
      );
    },
    messagesCurrent: transcriptCurrent,
    loadedTimestamp(directory: string, sessionID: string) {
      return meta.updated[key(directory, sessionID)];
    },
    attemptedTimestamp(directory: string, sessionID: string) {
      return meta.attempted[key(directory, sessionID)];
    },
    setAttempted(directory: string, sessionID: string, timestamp: number) {
      setMeta("attempted", key(directory, sessionID), timestamp);
    },
    seed(
      directory: string,
      sessionID: string,
      options?: {
        isNew?: boolean;
        provisional?: boolean;
        messagesPending?: boolean;
      },
    ) {
      const id = key(directory, sessionID);
      // Confirming the chat ends its activation request.
      if (!options?.provisional) endActivation(id);
      if (options?.messagesPending) {
        input.setMessages(sessionID, undefined);
        clear(directory, [sessionID]);
        if (options.provisional)
          activations.set(id, { ...beginSnapshot(id), superseded: false });
      } else if (options?.isNew && input.getMessages(sessionID) === undefined) {
        input.setMessages(sessionID, []);
        setMeta("limit", id, 0);
        setMeta("complete", id, !options.provisional);
        setMeta("loaded", id, true);
        setMeta(
          "authority",
          id,
          options.provisional ? "provisional" : "server",
        );
        // A new chat has published nothing the page could have missed.
        if (!options.provisional) setMeta("requested", id, input.streamCursor());
      }
    },
    restore(directory: string, sessionID: string) {
      if (disposed) return Promise.resolve(false);
      input.touch(directory, sessionID);
      applyPrefetch(directory, sessionID);
      if (input.getMessages(sessionID) !== undefined)
        return Promise.resolve(false);
      return hydrateReplica(directory, sessionID);
    },
    async sync(
      directory: string,
      sessionID: string,
      options?: { force?: boolean; initialLimit?: number },
    ): Promise<void> {
      if (disposed) return;
      const id = key(directory, sessionID);
      input.touch(directory, sessionID);
      const existingForce = forced.get(id);
      if (existingForce) return existingForce;
      const prior = options?.force
        ? (inflight.get(id) ?? messageLoads.get(id) ?? latestLoads.get(id))
        : undefined;
      const owner = lifetime(id);
      const execute = async () => {
        if (prior) await prior.catch(() => undefined);
        if (disposed || owner.signal.aborted) return;
        applyPrefetch(directory, sessionID);
        return run(id, async () => {
          const pending = input.prefetch.pending(directory, sessionID);
          if (pending) {
            await pending;
            if (disposed || owner.signal.aborted) return;
            applyPrefetch(directory, sessionID);
          }
          if (disposed || owner.signal.aborted) return;
          if (input.getMessages(sessionID) === undefined)
            void hydrateReplica(directory, sessionID);
          const hasSession = input.getSession(sessionID) !== undefined;
          // A current transcript is kept fresh by SSE. Session timestamps also
          // advance for status and metadata changes, so using them here turns any
          // remount or background sync into another full transcript download.
          // Anything else may be behind, so it loads now instead of waiting for
          // the stream. That includes a covered workspace still catching up: a
          // stream that looks connected after a phone resumes can be dead until
          // its heartbeat expires, so waiting for replay could leave an opened
          // chat outdated for seconds. Explicit force is reserved for
          // server.resync-required and mutations that knowingly invalidate the
          // transcript.
          const reuseCached =
            meta.limit[id] !== undefined &&
            transcriptCurrent(directory, sessionID) &&
            !options?.force;
          if (reuseCached && hasSession) return;
          const limit = Math.max(
            meta.limit[id] ?? 0,
            options?.initialLimit ?? messagePageSize,
          );
          const sessionRequest =
            hasSession && !options?.force
              ? Promise.resolve()
              : readSessionSnapshot(
                  (signal) => {
                    const client = input.infoClientFor(directory);
                    return client.session.get(
                      { sessionID },
                      { signal },
                    );
                  },
                  owner.signal,
                ).then((result) => {
                  if (
                    !disposed &&
                    !owner.signal.aborted &&
                    input.tracked(directory, sessionID) &&
                    result.data
                  )
                    input.upsertSession(result.data);
                });
          const messageRequest = reuseCached
            ? Promise.resolve()
            : loadMessages({ directory, sessionID, limit });
          await Promise.all([sessionRequest, messageRequest]);
        }).catch((error) => {
          if (disposed || owner.signal.aborted) return;
          if (isSessionNotFoundError(error))
            input.markMissing(directory, sessionID);
          throw error;
        });
      };
      // markStale cancels loads whose snapshot may predate an event-stream gap.
      // A caller still waiting on this sync gets the replacement load; a silent
      // success would leave its surface loading with nothing left to retry it.
      const request = execute().then(() => {
        if (
          disposed ||
          !owner.signal.aborted ||
          !meta.stale[id] ||
          !input.tracked(directory, sessionID)
        )
          return;
        return controller.sync(directory, sessionID, options);
      });
      if (options?.force) {
        forced.set(id, request);
        try {
          await request;
        } finally {
          if (forced.get(id) === request) forced.delete(id);
        }
      } else return request;
    },
    async reconcileLatest(directory: string, sessionID: string) {
      if (disposed || !input.tracked(directory, sessionID)) return;
      const id = key(directory, sessionID);
      const pending =
        forced.get(id) ??
        inflight.get(id) ??
        messageLoads.get(id) ??
        latestLoads.get(id);
      if (pending) return pending;
      // A replaced transcript is loaded whole again, not merged into.
      if (replaced.has(id)) return loadMessages({ directory, sessionID, limit: messagePageSize });
      const owner = lifetime(id);
      const snapshot = beginSnapshot(id);
      const request = (async () => {
        const page = await fetchMessages({
          client: input.messagesClientFor(directory),
          sessionID,
          limit: 1,
          signal: owner.signal,
        });
        if (
          disposed ||
          owner.signal.aborted ||
          !input.tracked(directory, sessionID) ||
          page.session.length === 0
        )
          return;
        const { messages, parts } = reconcileTranscriptSnapshot({
          live: input.getMessages(sessionID) ?? [],
          incoming: page.session,
          parts: page.part,
          liveParts: input.getParts,
          changes: snapshot.changes,
          prepend: true,
          complete: false,
        });
        batch(() => {
          input.setMessages(sessionID, messages);
          for (const item of parts) input.setParts(item.id, item.part);
          // The server's copies own prompts this client showed early, as with a
          // live message.updated; a late rollback must not erase them. A reply
          // confirms the prompt it answers, which this one-message window
          // leaves out once the reply exists.
          input.optimistic.forgetMessages(
            directory,
            sessionID,
            page.session.flatMap((message) => (message.role === "assistant" ? [message.id, message.parentID] : [message.id])),
          );
        });
        scheduleReplicaWrite(directory, sessionID);
        // A prompt the window leaves out, as when a question tool's or a
        // compaction's synthetic turn follows it, is confirmed by asking the
        // server for it. One the server lacks stays pending.
        const confirmed = await Promise.all(input.optimistic.pending(directory, sessionID).map(async (messageID) => {
          const found = await readSessionSnapshot(
            (signal) => input.messagesClientFor(directory).session.message({ sessionID, messageID }, { signal }),
            owner.signal,
          ).catch(() => undefined);
          return found?.data?.info.id === messageID ? [messageID] : [];
        }));
        if (disposed || owner.signal.aborted) return;
        const confirmedIDs = confirmed.flat();
        if (confirmedIDs.length) input.optimistic.forgetMessages(directory, sessionID, confirmedIDs);
      })()
        .catch((error) => {
          if (!disposed && !owner.signal.aborted) throw error;
        })
        .finally(() => {
          snapshot.finish();
          if (latestLoads.get(id) === request) latestLoads.delete(id);
        });
      latestLoads.set(id, request);
      return request;
    },
    recovering(directory: string, sessionID: string) {
      const id = key(directory, sessionID);
      return !!meta.loading[id] && !meta.prepending[id];
    },
    history: {
      async loadThroughMessage(
        directory: string,
        sessionID: string,
        messageID: string,
        signal: AbortSignal,
      ) {
        const id = key(directory, sessionID);
        input.touch(directory, sessionID);
        const owner = lifetime(id);
        while (
          !disposed &&
          !owner.signal.aborted &&
          !signal.aborted &&
          input.tracked(directory, sessionID)
        ) {
          if (
            input
              .getMessages(sessionID)
              ?.some((message) => message.id === messageID)
          )
            return true;
          const pending = messageLoads.get(id);
          if (pending) {
            await pending;
            continue;
          }
          const before = meta.cursor[id];
          if (meta.complete[id] || !before) return false;
          await loadMessages({
            directory,
            sessionID,
            limit: historyPageSize,
            before,
            mode: "prepend",
          });
          if (
            disposed ||
            owner.signal.aborted ||
            signal.aborted ||
            !input.tracked(directory, sessionID)
          )
            return false;
          if (
            input
              .getMessages(sessionID)
              ?.some((message) => message.id === messageID)
          )
            return true;
          if (!meta.complete[id] && meta.cursor[id] === before) {
            throw new Error(
              "History loading made no progress. Retry opening the message.",
            );
          }
        }
        return false;
      },
      more(directory: string, sessionID: string) {
        const id = key(directory, sessionID);
        return (
          input.getMessages(sessionID) !== undefined &&
          meta.limit[id] !== undefined &&
          !meta.complete[id] &&
          !!meta.cursor[id]
        );
      },
      loading(directory: string, sessionID: string) {
        return meta.prepending[key(directory, sessionID)] ?? false;
      },
      async loadMore(directory: string, sessionID: string, count?: number) {
        input.touch(directory, sessionID);
        const id = key(directory, sessionID);
        if (meta.loading[id] || meta.complete[id] || !meta.cursor[id]) return;
        await loadMessages({
          directory,
          sessionID,
          limit: count ?? scrollbackPageSize,
          before: meta.cursor[id],
          mode: "prepend",
        });
      },
    },
    dispose() {
      textRecovery.dispose();
      for (const [id, write] of replicaWrites) {
        clearTimeout(write.timer);
        const separator = id.indexOf("\n");
        void persistReplica(id.slice(0, separator), id.slice(separator + 1));
      }
      disposed = true;
      resetReconciliation?.abort();
      resetMarkers.clear();
      for (const id of lifetimes.keys()) invalidateRequests(id);
      replicaWrites.clear();
      replicaLoads.clear();
      activations.clear();
      inflight.clear();
      messageLoads.clear();
      setMeta({
        limit: {},
        cursor: {},
        complete: {},
        loading: {},
        loaded: {},
        authority: {},
        updated: {},
        attempted: {},
        prepending: {},
        stale: {},
      });
    },
  };
  return controller;
}
